// Song Flagging Service
// Allows attendees to flag songs they find inappropriate for DJ review

import { collection, doc, setDoc, getDocs, getDoc, updateDoc, query, where, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from './firebase';

export interface SongFlag {
  id: string;
  eventId: string;
  requestId: string;
  songTitle: string;
  songArtist: string;
  reason: string;
  flaggedBy: string; // attendee UUID
  flaggedAt: string; // ISO
  status: 'pending' | 'reviewed' | 'dismissed';
  djReviewedAt?: string;
  djAction?: 'removed' | 'kept' | 'blocked-flagger';
}

/**
 * Submit a flag for a song in an event.
 * Throws if attendee is blocked from flagging or has exceeded limit.
 */
export async function flagSong(
  eventId: string,
  requestId: string,
  songTitle: string,
  songArtist: string,
  reason: string,
  attendeeId: string
): Promise<void> {
  // Check if flagger is blocked
  const blocked = await isFlaggerBlocked(eventId, attendeeId);
  if (blocked) {
    throw new Error('You cannot flag songs at this event');
  }

  // Check rate limit (max 3 flags per attendee per event)
  const existing = await getAttendeeFlags(eventId, attendeeId);
  if (existing >= 3) {
    throw new Error('You have reached the maximum number of flags for this event');
  }

  // Deterministic doc ID: {eventId}_{requestId}_{flaggedBy} — enforces one flag per attendee per song
  const flagDocId = `${eventId}_${requestId}_${attendeeId}`;
  await setDoc(doc(db, 'songFlags', flagDocId), {
    eventId,
    requestId,
    songTitle,
    songArtist,
    reason,
    flaggedBy: attendeeId,
    flaggedAt: new Date().toISOString(),
    status: 'pending',
  });
}

/**
 * Get count of flags an attendee has submitted for an event.
 */
async function getAttendeeFlags(eventId: string, attendeeId: string): Promise<number> {
  const q = query(
    collection(db, 'songFlags'),
    where('eventId', '==', eventId),
    where('flaggedBy', '==', attendeeId)
  );
  const snap = await getDocs(q);
  return snap.size;
}

/**
 * Get all flags for an event (DJ view).
 */
export async function getFlagsForEvent(eventId: string): Promise<SongFlag[]> {
  const q = query(
    collection(db, 'songFlags'),
    where('eventId', '==', eventId)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as SongFlag));
}

/**
 * Get flag count for a specific request.
 */
export async function getFlagCountForRequest(eventId: string, requestId: string): Promise<number> {
  const q = query(
    collection(db, 'songFlags'),
    where('eventId', '==', eventId),
    where('requestId', '==', requestId)
  );
  const snap = await getDocs(q);
  return snap.size;
}

/**
 * Get flag counts for all requests in an event (batch).
 * Returns a Map of requestId -> count.
 */
export async function batchGetFlagCounts(eventId: string): Promise<Map<string, number>> {
  const flags = await getFlagsForEvent(eventId);
  const counts = new Map<string, number>();
  for (const flag of flags) {
    counts.set(flag.requestId, (counts.get(flag.requestId) || 0) + 1);
  }
  return counts;
}

/**
 * Review a flag (DJ action).
 */
export async function reviewFlag(
  flagId: string,
  action: 'removed' | 'kept' | 'blocked-flagger'
): Promise<void> {
  const ref = doc(db, 'songFlags', flagId);
  await updateDoc(ref, {
    status: 'reviewed',
    djReviewedAt: new Date().toISOString(),
    djAction: action,
  });
}

/**
 * Check if an attendee is blocked from flagging in an event.
 * Blocked flaggers are stored as an array field on the event doc.
 */
export async function isFlaggerBlocked(eventId: string, attendeeId: string): Promise<boolean> {
  const eventRef = doc(db, 'events', eventId);
  const snap = await getDoc(eventRef);
  if (!snap.exists()) return false;
  const data = snap.data();
  return Array.isArray(data.blockedFlaggers) && data.blockedFlaggers.includes(attendeeId);
}

/**
 * Block an attendee from flagging songs in an event.
 */
export async function blockFlagger(eventId: string, attendeeId: string): Promise<void> {
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    blockedFlaggers: arrayUnion(attendeeId),
  });
}

/**
 * Unblock an attendee from flagging songs in an event.
 */
export async function unblockFlagger(eventId: string, attendeeId: string): Promise<void> {
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    blockedFlaggers: arrayRemove(attendeeId),
  });
}
